import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { planoAdminService, usuarioAdminService } from '@/services/api';
import { AdminUser, PaginationParams, PaginationResponse, Plano } from '@/types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

export const AdminUsuariosPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { handleError, handleSuccess } = useErrorHandler();

  const [filters, setFilters] = useState<PaginationParams>({ page: 1, limit: 10 });
  const [searchText, setSearchText] = useState('');

  const [planoModalOpen, setPlanoModalOpen] = useState(false);
  const [perfilModalOpen, setPerfilModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [planoId, setPlanoId] = useState<number | ''>('');
  const [motivo, setMotivo] = useState('');
  const [perfilRole, setPerfilRole] = useState<'ADMIN' | 'USUARIO'>('USUARIO');

  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const { data: usuariosData, isLoading, error } = useQuery<PaginationResponse<AdminUser>>({
    queryKey: ['admin-usuarios', filters],
    queryFn: () => usuarioAdminService.list(filters),
  });

  const { data: planos = [] } = useQuery<Plano[]>({
    queryKey: ['admin-planos'],
    queryFn: () => planoAdminService.list(),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar usuários');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (usuariosData) setLoadError(null);
  }, [usuariosData]);

  const alterarPlanoMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { planoId: number; motivo?: string } }) =>
      usuarioAdminService.alterarPlano(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-usuarios'] });
      handleSuccess('Plano do usuário atualizado.');
      closePlanoModal();
    },
    onError: (err: any) => {
      handleError(err, 'Não foi possível alterar o plano');
    },
  });

  const alterarPerfilMutation = useMutation({
    mutationFn: ({ id, role }: { id: number; role: 'ADMIN' | 'USUARIO' }) =>
      usuarioAdminService.alterarPerfil(id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-usuarios'] });
      handleSuccess('Perfil do usuário atualizado.');
      closePerfilModal();
    },
    onError: (err: any) => {
      handleError(err, 'Não foi possível alterar o perfil');
    },
  });

  const openPlanoModal = (u: AdminUser) => {
    setSelectedUser(u);
    setPlanoId(u.plano?.id ?? '');
    setMotivo('');
    setPlanoModalOpen(true);
  };

  const closePlanoModal = () => {
    setPlanoModalOpen(false);
    setSelectedUser(null);
    setPlanoId('');
    setMotivo('');
  };

  const openPerfilModal = (u: AdminUser) => {
    setSelectedUser(u);
    setPerfilRole(u.role === 'ADMIN' ? 'ADMIN' : 'USUARIO');
    setPerfilModalOpen(true);
  };

  const closePerfilModal = () => {
    setPerfilModalOpen(false);
    setSelectedUser(null);
    setPerfilRole('USUARIO');
  };

  const submitPerfil = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    alterarPerfilMutation.mutate({ id: selectedUser.id, role: perfilRole });
  };

  const submitPlano = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    if (planoId === '') {
      toast.error('Selecione um plano.');
      return;
    }
    const body: { planoId: number; motivo?: string } = { planoId: Number(planoId) };
    if (motivo.trim()) body.motivo = motivo.trim();
    alterarPlanoMutation.mutate({ id: selectedUser.id, body });
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
        <p className="mt-1 text-sm text-gray-500">
          Com o plano <strong>Administrador</strong> (ou gestão de usuários ativa), você vê e gerencia todos os
          usuários do sistema. Com perfil <strong>ADMIN</strong> da instituição (sem esse plano), a lista fica
          limitada ao código do tenant (ex.: 277).
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de usuários</CardTitle>
              <CardDescription>
                {usuariosData?.pagination.total ?? 0} usuário(s)
              </CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por nome ou e-mail"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchText('')}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : !usuariosData?.data.length ? (
            <p className="text-gray-500 text-center py-8">Nenhum usuário encontrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Nome</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Código Instituição</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">E-mail</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Telefone</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Cargo</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Perfil</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Plano</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Expira em</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usuariosData.data.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{u.nome}</td>
                      <td className="px-3 py-2 text-gray-600 font-mono text-xs">{u.codigoTenant ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{u.email}</td>
                      <td className="px-3 py-2 text-gray-600">{u.telefone || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{u.cargo || '—'}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            u.role === 'ADMIN'
                              ? 'inline-flex rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800'
                              : 'inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700'
                          }
                        >
                          {u.role ?? '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2">{u.plano?.nome ?? '—'}</td>
                      <td className="px-3 py-2">{formatDate(u.planoExpiraEm)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          {currentUser?.id !== u.id && (
                            <Button type="button" variant="outline" size="sm" onClick={() => openPerfilModal(u)}>
                              Alterar perfil
                            </Button>
                          )}
                          <Button type="button" variant="outline" size="sm" onClick={() => openPlanoModal(u)}>
                            Alterar plano
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {usuariosData && usuariosData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2 items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page ?? 1) - 1)}
                  disabled={!usuariosData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="text-sm text-gray-700 px-2">
                  Página {usuariosData.pagination.page} de {usuariosData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page ?? 1) + 1)}
                  disabled={!usuariosData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Transition appear show={perfilModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !alterarPerfilMutation.isPending && closePerfilModal()}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">Alterar perfil</Dialog.Title>
                      {selectedUser && (
                        <p className="text-sm text-gray-500 mt-1">
                          {selectedUser.nome} — {selectedUser.email}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600"
                      onClick={closePerfilModal}
                      disabled={alterarPerfilMutation.isPending}
                      aria-label="Fechar"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <p className="text-sm text-gray-600 mb-4 rounded-md bg-gray-50 border border-gray-200 p-3">
                    <strong>ADMIN</strong> — acesso ao painel administrativo (menu e rotas /admin).{' '}
                    <strong>USUARIO</strong> — uso normal; recursos extras vêm do plano contratado, não do painel.
                  </p>

                  <form onSubmit={submitPerfil} className="space-y-4">
                    <div>
                      <Label htmlFor="novo-perfil">Perfil de acesso</Label>
                      <select
                        id="novo-perfil"
                        className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                        value={perfilRole}
                        onChange={(e) => setPerfilRole(e.target.value as 'ADMIN' | 'USUARIO')}
                        required
                      >
                        <option value="USUARIO">USUARIO — sem painel administrativo</option>
                        <option value="ADMIN">ADMIN — com painel administrativo</option>
                      </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={closePerfilModal} disabled={alterarPerfilMutation.isPending}>
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={alterarPerfilMutation.isPending}>
                        {alterarPerfilMutation.isPending ? 'Salvando…' : 'Confirmar'}
                      </Button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={planoModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !alterarPlanoMutation.isPending && closePlanoModal()}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">Alterar plano</Dialog.Title>
                      {selectedUser && (
                        <p className="text-sm text-gray-500 mt-1">
                          {selectedUser.nome} — {selectedUser.email}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600"
                      onClick={closePlanoModal}
                      disabled={alterarPlanoMutation.isPending}
                      aria-label="Fechar"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <form onSubmit={submitPlano} className="space-y-4">
                    <div>
                      <Label htmlFor="novo-plano">Plano</Label>
                      <select
                        id="novo-plano"
                        className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                        value={planoId === '' ? '' : String(planoId)}
                        onChange={(e) => setPlanoId(e.target.value === '' ? '' : Number(e.target.value))}
                        required
                      >
                        <option value="">Selecione…</option>
                        {planos.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nome} ({p.status})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="motivo-plano">Motivo (opcional)</Label>
                      <textarea
                        id="motivo-plano"
                        rows={3}
                        className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        placeholder="Ex.: upgrade contratado, teste encerrado…"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={closePlanoModal} disabled={alterarPlanoMutation.isPending}>
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={alterarPlanoMutation.isPending}>
                        {alterarPlanoMutation.isPending ? 'Salvando…' : 'Confirmar'}
                      </Button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};
